/**
 * Canonical URL shapes for the programmatic location pages.
 *
 *   /physiotherapy-in-noida                         city
 *   /physiotherapy-in-noida/sector-18               sub-region
 *   /physiotherapy-in-noida/sector-18/<name>-<id>   a clinic there
 *
 * The clinic segment carries a readable slug and ends with the 24-char id, so
 * the URL describes the page while lookup stays an exact id match.
 *
 * Build every location link through these helpers so the shape is defined once.
 */

import { CITIES, type City } from '@/data/cities';
import { SECTORS, type Sector } from '@/data/sectors';
import { getDistanceKm } from '@/utils/centreStats';
import { slugify } from '@/utils/slug';

export const CITY_PREFIX = 'physiotherapy-in-';

/** How far a clinic can sit from a sub-region centroid and still belong to it. */
const SECTOR_MATCH_KM = 12;
/** How far a clinic can sit from a city centroid and still belong to that city. */
const CITY_MATCH_KM = 60;

export function cityPath(citySlug: string): string {
  return `/${CITY_PREFIX}${citySlug}`;
}

export function sectorPath(citySlug: string, sectorSlug: string): string {
  return `/${CITY_PREFIX}${citySlug}/${sectorSlug}`;
}

/** Longest readable prefix; keeps URLs short enough to survive in SERPs. */
const MAX_SLUG_LEN = 60;

/** "PhysioBeats Healthcare" + id -> "physiobeats-healthcare-<id>" */
export function centreSegment(centreId: string, name?: string): string {
  const base = slugify(name ?? '').slice(0, MAX_SLUG_LEN).replace(/-+$/, '');
  return base ? `${base}-${centreId}` : centreId;
}

/** Pull the id back out of a clinic segment, whatever slug precedes it. */
export function parseCentreId(segment: string | undefined): string | undefined {
  const m = (segment ?? '').match(/([0-9a-fA-F]{24})$/);
  return m ? m[1] : undefined;
}

export function centrePath(
  citySlug: string,
  sectorSlug: string,
  centreId: string,
  name?: string,
): string {
  return `/${CITY_PREFIX}${citySlug}/${sectorSlug}/${centreSegment(centreId, name)}`;
}

/** The pre-nesting URL, kept so old links can be recognised and redirected. */
export function legacySectorPath(citySlug: string, sectorSlug: string): string {
  return `/${CITY_PREFIX}${citySlug}-${sectorSlug}`;
}

interface LocatableCentre {
  id: string;
  name?: string;
  city?: string;
  location?: { lat: number; lng: number };
}

function nearestCity(lat: number, lng: number): { city: City; km: number } | undefined {
  let best: { city: City; km: number } | undefined;
  for (const c of CITIES) {
    const km = getDistanceKm(lat, lng, c.lat, c.lng);
    if (!best || km < best.km) best = { city: c, km };
  }
  return best;
}

function cityFor(centre: LocatableCentre): City | undefined {
  if (!centre.location) return undefined;
  const best = nearestCity(centre.location.lat, centre.location.lng);
  if (!best || best.km > CITY_MATCH_KM) return undefined;

  // A stated city that isn't even the nearest one means the address and the
  // coordinates disagree. Publishing a locality page off contradictory data
  // would put the clinic in the wrong neighbourhood, so decline instead.
  const named = centre.city ? slugify(centre.city) : '';
  const stated = CITIES.find((c) => c.slug === named);
  if (stated && stated.slug !== best.city.slug) return undefined;

  return best.city;
}

/**
 * The city + sub-region a clinic belongs to, or null when it sits outside every
 * mapped locality. Callers fall back to /centres/<id> rather than invent a
 * sub-region that doesn't exist.
 */
export function resolveCentreLocality(
  centre: LocatableCentre,
): { city: City; sector: Sector } | null {
  const city = cityFor(centre);
  if (!city || !centre.location) return null;
  let best: { sector: Sector; km: number } | undefined;
  for (const s of SECTORS) {
    if (s.citySlug !== city.slug) continue;
    const km = getDistanceKm(centre.location.lat, centre.location.lng, s.lat, s.lng);
    if (!best || km < best.km) best = { sector: s, km };
  }
  if (!best || best.km > SECTOR_MATCH_KM) return null;
  return { city, sector: best.sector };
}

/** Locality-scoped clinic URL, or undefined to use the default /centres/<id>. */
export function centreSeoPath(centre: LocatableCentre): string | undefined {
  const found = resolveCentreLocality(centre);
  return found
    ? centrePath(found.city.slug, found.sector.slug, centre.id, centre.name)
    : undefined;
}
